import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Module } from '../../../types';
import { useClubSlug } from '../../Clubs/hooks';
import { VoucherType } from '../types';

interface ValidateResult {
    value: number;
    value_used: number;
    percentage?: number;
    type: VoucherType;
}

/**
 * Checks a voucher code against the club and reports what it is worth. Each caller keeps
 * its own result, so the code field and the saved-voucher list each report on their own
 * action instead of one speaking for the other.
 */
export default function useVoucherValidation(type: Module) {
    const { t } = useTranslation('voucher');
    const clubSlug = useClubSlug();
    const { watch } = useFormContext();
    const [validateResult, setValidateResult] = useState<ValidateResult | undefined>();
    const [isInvalid, setIsInvalid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const voucher_code = watch('voucher_code');

    useEffect(() => {
        setValidateResult(undefined);
        setIsInvalid(false);
    }, [voucher_code]);

    const renderVoucherValue = useMemo(() => {
        switch (validateResult?.type) {
            case 'value':
                return `${(validateResult.value - validateResult.value_used).toFixed(2)} €`;
            case 'discount':
                return `${validateResult.percentage} %`;
            case 'card':
                // subtracting floats can yield e.g. 52.900000000000006 — round before display/pluralization
                return t('fields.charges', {
                    count: Math.round((validateResult.value - validateResult.value_used) * 100) / 100
                });
            default:
                return '';
        }
    }, [validateResult, t]);

    const validate = useCallback(
        (_voucher_code?: string) => {
            const voucherCodeToValidate = _voucher_code || voucher_code;
            if (!voucherCodeToValidate || typeof voucherCodeToValidate !== 'string') return;

            setIsLoading(true);
            setIsInvalid(false);
            const normalizedCode = voucherCodeToValidate.trim().replace(/\s+/g, '').toLowerCase();
            fetch(
                `${process.env.NEXT_PUBLIC_API_HOST}club/${clubSlug}/voucher/${normalizedCode}/validate?type=${type}`,
                {
                    headers: { Accept: 'application/json' }
                }
            )
                .then((res) => {
                    if (!res.ok) {
                        setIsInvalid(true);
                    } else {
                        return res.json();
                    }
                })
                .then(setValidateResult)
                .finally(() => setIsLoading(false));
        },
        [voucher_code, clubSlug, type]
    );

    return { validate, validateResult, isInvalid, isLoading, renderVoucherValue };
}
